import React, { useState, FormEvent, ChangeEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from 'services/auth/authService';
import { toast } from 'react-toastify';
import Spinner from 'components/common/Spinner';
import { ReactComponent as Logomark } from 'assets/images/logos/Logomark.svg';
import { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'contexts/ThemeContext';
import { setAccessToken } from 'utils/auth/auth';

interface LocationState {
  userId: string;
  email: string;
  password: string;
}

interface ErrorMessages {
  otp?: string;
  general?: string;
}

const VerifyOTP: React.FC = () => {
  const { t } = useTranslation();
  const [otp, setOtp] = useState<string>('');
  const [errorMessages, setErrorMessages] = useState<ErrorMessages>({});
  const [localLoading, setLocalLoading] = useState<boolean>(false);
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { userId, email, password } = (location.state as LocationState) || {};

  const validateForm = (): boolean => {
    const errors: ErrorMessages = {};
    const otpRegex = /^\d{6}$/;

    if (!otp) {
      errors.otp = t('verifyOTP.errors.otpRequired');
    } else if (!otpRegex.test(otp)) {
      errors.otp = t('verifyOTP.errors.otpInvalid');
    }

    setErrorMessages(errors);
    return Object.keys(errors).length === 0;
  };

  const handleVerifyOTP = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLocalLoading(true);
      const response: AxiosResponse = await authService.verifyOTP(userId, otp);

      if (response.status === 200) {
        // Automatically log in the user after OTP verification
        const loginResponse = await authService.login(email, password);
        if (loginResponse.status === 200 && loginResponse.data.accessToken) {
          setAccessToken(loginResponse.data.accessToken);
          const isProduction = window.location.protocol === 'https:';
          const secureFlag = isProduction ? '; secure' : '';
          document.cookie = `refreshToken=${loginResponse.data.refreshToken}; path=/; max-age=31536000${secureFlag}; samesite=strict`;
          localStorage.setItem('justLoggedIn', 'true');
          navigate('/welcome');
        } else {
          throw new Error(loginResponse.data.message || t('verifyOTP.errors.loginFailed'));
        }
      } else {
        throw new Error(response.data.message || t('verifyOTP.errors.verificationFailed'));
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || t('verifyOTP.errors.verificationFailedRetry');
      setErrorMessages({ general: errorMessage });
      toast.error(errorMessage);
    } finally {
      setLocalLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/registration');
  };

  const inputClassName = `w-full px-3 py-2 text-base leading-6 rounded-fc outline-none transition-all ${
    isDark
      ? 'bg-fc-dark-surface-input text-fc-dark-text-primary placeholder-fc-dark-text-muted fc-input-gradient-border-dark'
      : 'bg-white text-fc-text-primary placeholder-fc-text-muted fc-input-gradient-border'
  }`;

  const inputStyle = { '--fc-input-bg': isDark ? '#0F172B' : '#FFFFFF' } as React.CSSProperties;

  const labelClassName = `block text-base font-medium leading-6 ${
    isDark ? 'text-fc-dark-text-primary' : 'text-fc-text-primary'
  }`;

  return (
    <div
      className={`min-h-screen flex items-center justify-center p-4 font-geist transition-colors duration-300 ${
        isDark ? 'bg-fc-dark' : 'bg-fc-light'
      }`}
    >
      {/* Back button - top left */}
      <button
        type="button"
        onClick={handleBack}
        className={`absolute top-6 left-6 flex items-center gap-2 text-sm font-medium leading-5 transition-colors ${
          isDark
            ? 'text-fc-dark-text-secondary hover:text-fc-dark-text-primary'
            : 'text-fc-text-secondary hover:text-fc-text-primary'
        }`}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12.5 15L7.5 10L12.5 5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {t('verifyOTP.buttons.back')}
      </button>

      <div className="w-full max-w-[480px] flex flex-col items-center gap-6">
        {/* Logo */}
        <Logomark className="w-20 h-20" />

        {/* Title + Description */}
        <div className="w-full text-center space-y-4">
          <h1
            className={`text-2xl font-bold leading-8 ${
              isDark ? 'text-fc-dark-text-primary' : 'text-fc-text-primary'
            }`}
          >
            {t('verifyOTP.titles.verifyEmail')}
          </h1>
          <p
            className={`text-sm font-normal leading-5 ${
              isDark ? 'text-fc-dark-text-secondary' : 'text-fc-text-secondary'
            }`}
          >
            {t('verifyOTP.messages.enterCode')}{' '}
            <br />
            <span className={isDark ? 'text-[#51A2FF]' : 'text-fc-blue'}>
              {email}
            </span>
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleVerifyOTP} className="w-full space-y-4">
          {/* Verification code */}
          <div className="space-y-1">
            <label htmlFor="otp" className={labelClassName}>
              {t('verifyOTP.labels.verificationCode')}
            </label>
            <input
              type="text"
              id="otp"
              placeholder={t('verifyOTP.labels.otpPlaceholder')}
              value={otp}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setOtp(e.target.value)}
              required
              inputMode="numeric"
              maxLength={6}
              autoComplete="one-time-code"
              style={inputStyle}
              className={inputClassName}
            />
            {errorMessages.otp && (
              <p className="text-xs text-red-500 mt-1">{errorMessages.otp}</p>
            )}
          </div>

          {/* General error */}
          {errorMessages.general && (
            <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-3 rounded">
              <p className="text-sm text-red-700 dark:text-red-400">{errorMessages.general}</p>
            </div>
          )}

          {/* Verify Email Button */}
          <button
            type="submit"
            disabled={localLoading}
            className="w-full h-12 bg-fc-gradient-btn text-white text-base font-medium leading-6 rounded-fc-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {localLoading ? (
              <>
                <Spinner size="small" color="white" />
                <span>{t('verifyOTP.buttons.verifying')}</span>
              </>
            ) : (
              t('verifyOTP.buttons.verifyEmail')
            )}
          </button>

          {/* Spam folder note */}
          <div
            className={`flex items-start gap-2 p-3 rounded-fc border ${
              isDark
                ? 'bg-fc-dark-surface-input/40 border-fc-dark-border text-fc-dark-text-secondary'
                : 'bg-fc-light-surface border-fc-border text-fc-text-secondary'
            }`}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="flex-shrink-0 mt-0.5"
              aria-hidden="true"
            >
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
            <p className="text-xs font-normal leading-5">
              {t('verifyOTP.messages.spamNote')}
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VerifyOTP;
